(ns app.front.view.property.subs
  (:require [app.domain.address.model :as address-model]
            [app.domain.application.condition.model :as condition-model]
            [app.domain.application.model :as app-model]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.fee.model :as fee-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.licence.generic-model :as gl-model]
            [app.domain.licence.model :as licence-model]
            [app.domain.location.model :as location-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.party.model :as party-model]
            [app.domain.periodic-inspection.model :as periodic-inspection-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.util :as futil]
            [app.front.view.admin.permissions :as admin-permissions]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.create-application.gis-search :as-alias gis-search]
            [app.front.view.fee.list :as fee-list]
            [app.front.view.fee.summary :as fee-summary]
            [app.front.view.inspections.summary-subsection :as inspections-summary-subsection]
            [app.front.view.licence.util :as licence-util]
            [app.front.view.project.conditions :as conditions]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.date-util :as date-util]
            [core.front.event :refer [>evt]]
            [core.front.feature-flags :as-alias ff]
            [core.front.href :as href]
            [core.front.i18n :as i18n]
            [core.front.routing :as routing]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def min-address-search-length 3)

(def search-tracking-interceptor
  (rf/->interceptor
    :id ::search-tracking
    :after (fn [context]
             (let [old-db (get-in context [:coeffects :db])
                   new-db (get-in context [:effects :db])]
               (if new-db
                 (let [new-search-value (get-in new-db [::gis-search/address-search-results :search-value])
                       old-pending?     (get-in old-db [::gis-search/address-search-results :pending?])
                       new-pending?     (get-in new-db [::gis-search/address-search-results :pending?])
                       prev-value       (get-in new-db [::address-search-state :previous-search-value])
                       search-started?  (get-in old-db [::address-search-state :search-started?])]

                   (cond-> context
                     (and new-search-value
                          (not= new-search-value prev-value))
                     (update-in [:effects :dispatch-n]
                                (fnil into [])
                                [[::set-previous-search-value new-search-value]
                                 [::set-search-started false]
                                 [::clear-address-search-result]
                                 [::clear-geocoded-reference]])

                     (and (not old-pending?) new-pending? (not search-started?))
                     (update-in [:effects :dispatch-n]
                                (fnil into [])
                                [[::set-search-started true]])))
                 context)))))

(rf/reg-global-interceptor search-tracking-interceptor)

(rf/reg-event-fx ::get-property-data
  (fn [{:keys [db]} [_ property-id]]
    {:db           (-> db
                       (assoc ::state {:loading? true}))
     :comm/command {:command      :property/get
                    :method       :get
                    :data         (util/mmap property-id)
                    :on-success   [::get-property-data.success]
                    :on-not-found [::routing/reset-view]}
     :dispatch     [::municipality/load-municipalities true]}))

(rf/reg-event-fx ::get-property-data-by-subdiv
  (fn [{:keys [db]} [_ id-domain verified-id]]
    {:db           (-> db
                       (assoc ::state {:loading? true}))
     :comm/command {:command      :property/get
                    :method       :get
                    :data         (util/mmap id-domain verified-id)
                    :on-success   [::get-property-data.success]
                    :on-not-found [::routing/reset-view]}
     :dispatch     [::municipality/load-municipalities true]}))

(rf/reg-event-fx ::get-property-data.success
  (fn [{:keys [db]} [_ status {:keys [property-id entities descriptions address-ids-in-use]}]]
    {:db (-> db
             (assoc-in [::state :loading?] false)
             (assoc-in [::state :property-id] property-id)
             (assoc-in [::state :ws-descriptions] descriptions)
             (assoc-in [::state :address-ids-in-use] (set address-ids-in-use))
             (p/dissoc-in [::state :loading-attachments-for-type?]) ;; clear loading state for attachments to allow re-fetching
             (p/dissoc-in [:app.front.ui.section/state "section--property-workspace-attachments"])) ;; force section states to be reset (so that user needs to open the attachment section to trigger re-fetching)
     :fx [[:dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]]
          [:dispatch [::get-property-relations property-id]]]}))

(rf/reg-event-fx ::update-data-from-gis
  (fn [{:keys [db]} [_ property-id]]
    {:db           (assoc-in db [::state :loading?] true)
     :comm/command {:command    :property/update-data-from-gis
                    :method     :post
                    :data       (util/mmap property-id)
                    :on-success [::update-data-from-gis.success]
                    :on-error   [::update-data-from-gis.error]}}))

(rf/reg-event-fx ::delete-property
  (fn [{:keys [db]} [_ property-id]]
    {:db           (assoc-in db [::state :loading?] true)
     :comm/command {:command      :property/delete-property-record
                    :method       :post
                    :data         {:property-id property-id}
                    :on-success   [::delete-property-record.success]
                    :on-error     [::delete-property-record.error]
                    :on-forbidden [::submit-failure.forbidden]}}))

(rf/reg-event-fx ::delete-property-record.success
  (fn [{:keys [db]} [_ status]]
    (if (= status 200)
      {:db            (assoc-in db [::state :loading?] false)
       :fx            [[:dispatch [:notifications/add :toast :success :success
                                   :property-view/property-deleted]]]
       :route/replace {:route :view/property-dashboard}}
      {:db db})))

(rf/reg-event-fx ::delete-property-record.error
  (fn [{:keys [db]} _]
    {:db (assoc-in db [::state :loading?] false)
     :fx [[:dispatch [:notifications/add :toast :error :error/error :error/property-delete-failed]]]}))

(rf/reg-event-fx ::submit-failure.forbidden
  (fn [{:keys [db _]}]
    {:db (assoc-in db [::state :loading?] false)
     :fx [[:dispatch [:notifications/add :toast :error :error/error :error/property-has-linked-entities]]]}))

(rf/reg-event-fx ::set-property-verified
  (fn [{:keys [db]} _]
    {:comm/command {:command        :property/set-property-verified
                    :method         :post
                    :data           {:property-ids [(get-in db [::state :property-being-verified])]}
                    :on-success     [::entities/apply-changes]
                    :notify         {:success :property/verification-success}
                    :on-bad-request [::set-property-verified.error]}}))

(rf/reg-event-db ::set-property-verified.error
  (fn [db [_ _status body]]
    (assoc-in db [::state :property-verification-conflicts] (:conflicting-property-ids body))))


(rf/reg-event-fx ::update-data-from-gis.success
  (fn [{:keys [db]} [_ status {:keys [changes gis-identity-confirmed?]}]]
    {:db (assoc-in db [::state :loading?] false)
     :fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch (if gis-identity-confirmed?
                       [:notifications/add :toast :success :success :property/updated-from-gis]
                       [:notifications/add :toast :warning :warning/warning :property/gis-identity-not-confirmed])]]}))

(rf/reg-event-fx ::update-data-from-gis.error
  (fn [{:keys [db]} _]
    {:db (assoc-in db [::state :loading?] false)
     :fx [[:dispatch [:notifications/add :toast :error :error :error/generic-gis-error]]]}))

(rf/reg-event-fx ::fetch-attachments-for-permit-type
  (fn [{:keys [db]} [_ permit-type]]
    ;; Check if we are already have fetched attachments for the given permit type.
    (when-not (-> db ::state :loading-attachments-for-type? (contains? permit-type))
      (let [property-id (-> db ::state :property-id)]
        {:db           (assoc-in db [::state :loading-attachments-for-type? permit-type] true)
         :comm/command {:command    :property/attachments-for-permit-type
                        :method     :get
                        :data       (util/mmap property-id
                                               permit-type)
                        :on-success [::fetch-attachments-for-permit-type.success permit-type]
                        :on-error   [::fetch-attachments-for-permit-type.error permit-type]}}))))

(rf/reg-event-fx ::fetch-attachments-for-permit-type.success
  (fn [{:keys [db]} [_ permit-type status {:keys [entities]}]]
    {:db       (assoc-in db [::state :loading-attachments-for-type? permit-type] false)
     :dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]}))

(rf/reg-event-fx ::fetch-attachments-for-permit-type.error
  (fn [{:keys [db]} [_ permit-type]]
    {:db (p/dissoc-in db [::state :loading-attachments-for-type? permit-type]) ;; in case of error, remove loading state for the type, so it can be retried again.
     :fx [[:dispatch [:notifications/add :toast :error :error :error/fetch-attachments-failed-for-permit-type]]]}))

(rf/reg-event-db ::change-complaint-month-selection
  (fn [db [_ months]]
    (assoc-in db [:complaints-state :months] months)))

(rf/reg-event-db ::toggle-show-invalid-licences?
  (fn [db _]
    (update-in db [::state ::show-invalid-licences?] not)))

(rf/reg-event-db ::set-selected-permit-type-tab
  (fn [db [_ tab]]
    (assoc-in db [::state ::selected-permit-type-tab] tab)))

(rf/reg-event-db ::set-selected-workspace-attachments-permit-type-tab
  (fn [db [_ permit-type]]
    (assoc-in db [::state :selected-workspace-attachments-permit-type-tab] permit-type)))

(rf/reg-event-fx ::set-editing
  (fn [{:keys [db]} [_ group-id editing?]]
    (merge {:db (cond-> (assoc-in db [::state ::editing?] editing?)
                  (not editing?)
                  (-> (update ::map-state dissoc :custom-center)
                      (dissoc ::coordinate-edit-state)
                      (update ::address-search-state dissoc :selected-coordinates :geocoded-reference-point)
                      (update ::state dissoc ::edit-details)))}
           (when editing?
             {:comm/command {:command    :group/property-attribute-templates
                             :method     :get
                             :data       {:group-id group-id}
                             :on-success [::entities/merge-entity-db]}}))))

(rf/reg-event-fx ::set-attribute-value
  (fn [_ [_ property-id template-id val-attr value]]
    (if-some [v (if (map? value)
                  (when (some str-util/blank->nil (vals value)) value)
                  (str-util/blank->nil value))]
      {:comm/command {:command    :property/set-attribute-value
                      :data       (util/mmap {val-attr v} property-id template-id)
                      :on-success [::entities/apply-changes]
                      :notify     {:success :property-view/attribute-saved}}}
      {:comm/command {:command    :property/remove-attribute
                      :data       (util/mmap property-id template-id)
                      :on-success [::entities/apply-changes]
                      :notify     {:success :property-view/attribute-removed}}})))

(rf/reg-event-fx ::set-attribute-value-debounce
  (fn [_ [_ property-id template-id attr value]]
    {:dispatch-debounce {:id      ::set-attribute-value
                         :timeout 300
                         :action  :dispatch
                         :event   [::set-attribute-value property-id template-id attr value]}}))

(rf/reg-event-db ::set-edit-detail
  (fn [db [_ k v]]
    (assoc-in db [::state ::edit-details k] v)))

(rf/reg-event-db ::set-edit-address
  (fn [db [_ address]]
    (if address
      (assoc-in db [::state ::edit-details :address] address)
      (update-in db [::state ::edit-details] dissoc :address))))

(rf/reg-event-db ::clear-edit-details
  (fn [db _]
    (update db ::state dissoc ::edit-details)))

(rf/reg-event-fx ::set-primary-address
  (fn [_ [_ property-id address-id]]
    {:comm/command {:command    :property/set-primary-address
                    :method     :post
                    :data       {:property-id property-id
                                 :address-id  address-id}
                    :on-success [::set-primary-address.success]
                    :notify     {:success :property-view/primary-address-updated
                                 :error   :property-view/primary-address-update-failed}}}))

(rf/reg-event-fx ::set-primary-address.success
  (fn [{:keys [db]} [_ status changes]]
    {:fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch [::get-property-relations (get-in db [::state :property-id])]]]}))

(rf/reg-event-fx ::verify-address
  (fn [_ [_ property-id address-id]]
    {:comm/command {:command    :property/verify-address
                    :method     :post
                    :data       {:property-id property-id
                                 :address-id  address-id}
                    :on-success [::verify-address.success]
                    :notify     {:success :property-view/address-verified-successfully
                                 :error   :property-view/address-verification-failed}}}))

(rf/reg-event-fx ::verify-address.success
  (fn [{:keys [db]} [_ status response]]
    {:fx [[:dispatch [::entities/apply-changes status response]]
          [:dispatch [::get-property-relations (get-in db [::state :property-id])]]]}))

(rf/reg-event-db ::set-add-address-modal-open
  (fn [db [_ open?]]
    (if open?
      (-> db
          (assoc-in [::state :add-address-modal-open?] true)
          (assoc-in [::state :add-address] ""))
      (update db ::state dissoc :add-address-modal-open? :add-address))))

(rf/reg-sub ::add-address-modal-open?
  :<- [::state]
  (fn [state _]
    (get state :add-address-modal-open? false)))

(rf/reg-event-db ::set-add-address
  (fn [db [_ address]]
    (assoc-in db [::state :add-address] address)))

(rf/reg-sub ::add-address
  :<- [::state]
  (fn [state _]
    (get state :add-address "")))

(rf/reg-event-fx ::add-additional-address
  (fn [_ [_ property-id full-address parse-address]]
    {:dispatch [:comm/command-monitored
                {:command    :property/add-address
                 :method     :post
                 :data       {:property-id property-id
                              :address     (parse-address full-address)}
                 :on-success [::add-additional-address.success]
                 :on-error   [::add-additional-address.error]}]}))

(rf/reg-event-fx ::add-additional-address.success
  (fn [{:keys [db]} [_ status changes]]
    {:db (update db ::state dissoc :add-address-modal-open? :add-address)
     :fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch [:notifications/add :toast :success :success :property-view/address-added-successfully]]]}))

(rf/reg-event-fx ::add-additional-address.error
  (fn [_ _]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property-view/address-addition-failed]]]}))

(rf/reg-event-fx ::remove-address
  (fn [_ [_ property-id address-id]]
    {:dispatch [:comm/command-monitored
                {:command    :property/remove-address
                 :method     :post
                 :data       {:property-id property-id
                              :address-id  address-id}
                 :on-success [::entities/apply-changes]
                 :notify     {:success :property-view/address-removed-successfully
                              :error   :property-view/address-removal-failed}}]}))

(rf/reg-event-fx ::save-edit-details
  (fn [{:keys [db]} [_ property-id]]
    (let [edits (get-in db [::state ::edit-details])]
      {:dispatch [:comm/command-monitored
                  {:command    :property/edit-details
                   :method     :post
                   :data       (assoc edits :property-id property-id)
                   :on-success [::edit-details.success]
                   :notify     {:success :property-view/details-saved
                                :error   :error/property-details-save-failed}}]})))

(rf/reg-event-fx ::edit-details.success
  (fn [{:keys [db]} [_ status changes]]
    {:db (update db ::state dissoc ::edit-details ::editing?)
     :fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch [::get-property-relations (get-in db [::state :property-id])]]]}))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::property-id
  :<- [::state]
  (fn [{:keys [property-id]} _]
    property-id))

(rf/reg-sub ::address-ids-in-use
  :<- [::state]
  :-> :address-ids-in-use)

(rf/reg-sub ::property
  :<- [::property-id]
  :<- [::entities/context]
  (fn [[property-id ctx] _]
    (entities/find-entity ctx property-id)))

(rf/reg-sub ::property-href
  :<- [::property-id]
  (fn [property-id _]
    (href/href :view/property {:db-id property-id})))

(rf/reg-sub ::property-authority?
  :<- [::property]
  :<- [:session/authority-groups]
  :<- [:session/admin?]
  (fn [[property user-authority-groups admin?] _]
    (or admin?
        (municipality-model/municipality-in-scope? (:property/municipality property) user-authority-groups))))

(rf/reg-sub ::workspaces
  :<- [::property]
  (fn [property _]
    (->> property
         :workspace/_properties
         (remove periodic-inspection-model/periodic-inspection-workspace-in-finished-state?))))

(rf/reg-sub ::workspaces-by-permit-type
  :<- [::workspaces]
  (fn [workspaces [_ permit-type]]
    (->> workspaces
         (filter #(= (:workspace/permit-type %) permit-type))
         (remove periodic-inspection-model/periodic-inspection-workspace-in-finished-state?))))

(rf/reg-sub ::workspace-descriptions
  :<- [::state]
  :-> :ws-descriptions)

;; Table rows for the property's workspaces. With a `permit-type` argument only that permit type's
;; workspaces are included; without one, every workspace linked to the property is.
(rf/reg-sub ::workspace-items
  (fn [[_ permit-type] _]
    [(rf/subscribe [::tr])
     (if permit-type
       (rf/subscribe [::workspaces-by-permit-type permit-type])
       (rf/subscribe [::workspaces]))
     (rf/subscribe [:language])
     (rf/subscribe [:branding/as-string])
     (rf/subscribe [::workspace-descriptions])])
  (fn [[tr workspaces lang branding descriptions] _]
    (->> workspaces
         (sort-by :entity/created)
         (map (fn [ws]
                (let [workspace-id     (:workspace/id ws)
                      tz               (-> ws :domain/authority-group :group/tz)
                      application      (workspace-model/workspace-application ws)
                      pre-consultation (workspace-model/own-or-prior-pre-consultation ws)
                      execution        (workspace-model/workspace-execution ws)
                      periodic-parent  (workspace-model/workspace-periodic-parent ws)
                      owner-parties    (->> [application pre-consultation execution periodic-parent]
                                            (mapcat party-model/phase-parties)
                                            (filter #(party-model/has-role? % :role/property-owner))
                                            (map #(party-model/simple-name % lang))
                                            distinct)
                      owners           (str/join ", " owner-parties)
                      state            (if-let [state (:domain/state (workspace-model/workspace-target ws))]
                                         (tr [state])
                                         (tr [:workspace.hidden/status]))
                      category         (config-model/category-name ws lang)
                      work-type        (cond
                                         application
                                         (app-model/work-type-name application lang)
                                         periodic-parent
                                         (periodic-inspection-model/work-type-name periodic-parent lang)
                                         :else
                                         (config-model/work-type-name ws lang))
                      work-target      (cond
                                         application
                                         (app-model/work-target-name application lang)
                                         periodic-parent
                                         (periodic-inspection-model/work-target-name periodic-parent lang)
                                         :else
                                         (config-model/work-target-name ws lang))
                      date             (or (some->> (verdict-model/application-verdicts application)
                                                    first
                                                    :verdict/issue-date)
                                           (some-> execution :entity/parent :entity/created))
                      normal-ws-link   (href/branded-href branding :view/workspace {:workspace-id workspace-id})
                      ws-link          (ws-util/workspace-or-resume-application-href ws normal-ws-link)
                      resume-app?      (ws-util/resume-application? ws)]
                  (merge (select-keys application [:application/approved
                                                   :application/submitted])
                         ;; :db/id is what the workspace reassign section selects rows by
                         (select-keys ws [:db/id :workspace/created])
                         (p/assoc-if {:workspace/municipal-case-id (workspace-model/case-id ws)}
                                     :verdict/issue-date (some-> (verdict-model/issued-or-first-verdict application)
                                                                 :verdict/issue-date)
                                     :execution-created (:entity/created execution)
                                     :description (get descriptions workspace-id))
                         (util/mmap workspace-id state category owners date tz ws-link work-type work-target)
                         {:resume-application? resume-app?})))))))

(rf/reg-sub ::loading?
  :<- [::state]
  (fn [state _] (:loading? state true)))

(rf/reg-sub ::loading-attachments-for-type?
  :<- [::state]
  (fn [state [_ permit-type]]
    (get-in state [:loading-attachments-for-type? permit-type] false)))

(rf/reg-sub ::tr
  :<- [:tr]
  :<- [::property]
  (fn [[tr property] _]
    (->> property
         :property/municipality
         :scope/_municipality
         (map :group/_scopes)
         (map :entity/parent)
         ;; only one domain group should be present
         first
         (property-model/property-tr-fn tr))))

(rf/reg-sub ::tr-for-permit-type
  :<- [:tr]
  (fn [tr [_ permit-type]]
    (i18n-model/wrap-tr-fn (i18n-model/permit-type-specific-keys permit-type) tr)))

(rf/reg-sub ::execution-phases
  :<- [::workspaces]
  (fn [workspaces _]
    (->> workspaces
         (keep workspace-model/workspace-execution))))

(rf/reg-sub ::inspections
  :<- [::workspaces]
  (fn [workspaces _]
    (->> workspaces
         (mapcat workspace-model/inspections))))

(rf/reg-sub ::deficiencies
  :<- [::inspections]
  :<- [::i18n/full-locale]
  :<- [:language]
  (fn [[inspections locale lang] _]
    (->> inspections
         (mapcat :entity/_parent)
         (filter (eu/is-type-fn :entity.type/inspection-deficiency))
         (inspections-summary-subsection/group-deficiencies locale lang))))

(rf/reg-sub ::orders
  :<- [::execution-phases]
  :<- [::i18n/full-locale]
  :<- [:language]
  (fn [[execution-phases locale lang] _]
    (->> execution-phases
         (mapcat :entity/_parent)
         (filter (eu/is-type-fn :entity.type/inspection-order))
         (inspections-summary-subsection/group-orders locale lang))))

(rf/reg-sub ::bills
  :<- [::workspaces]
  (fn [workspaces _]
    (->> workspaces
         (mapcat :entity/_parent)
         (filter fee-list/show-bills-for-phase-entity?)
         (mapcat :entity/_parent)
         (filter (eu/is-type-fn :entity.type/bill))
         fee-summary/group-bills)))

(rf/reg-sub ::conditions
  :<- [::workspaces]
  (fn [workspaces _]
    (->> workspaces
         (mapcat (fn [workspace]
                   (->> (workspace-model/workspace-target workspace)
                        :entity/_parent
                        (filter (eu/is-type-fn :entity.type/application-condition))
                        (remove condition-model/general-condition?))))
         (group-by (comp :entity/parent :entity/parent))
         (p/map-vals conditions/sort-and-index)
         (p/map-vals (partial filter (comp (complement :application.condition/closed)
                                           second)))
         (filter (comp seq second))
         (into {}))))

(rf/reg-sub ::complaints
  :<- [::property]
  (fn [property _]
    (-> property :complaint/_property)))

(rf/reg-sub ::complaints-state
  (fn [db _]
    (-> db :complaints-state)))

(rf/reg-sub ::complaints-months
  :<- [::complaints-state]
  (fn [complaints-state _]
    (-> complaints-state :months)))

(rf/reg-sub ::complaint-items
  :<- [::complaints]
  :<- [::complaints-months]
  :<- [:language]
  :<- [:tr]
  (fn [[complaints months lang tr] _]
    (let [selected-months     (cond (number? months) months
                                    (keyword? months) 0
                                    :else 12)
          filtered-complaints (if (> selected-months 0)
                                (->> complaints
                                     (filter #(date-util/on-or-after?
                                                (-> % :entity/created)
                                                (date-util/add-months-to-date (js/Date.) (- selected-months)))))
                                complaints)]
      (->> filtered-complaints
           (sort-by :entity/created)
           (map (fn [complaint]
                  (let [id                      (:db/id complaint)
                        complaint-id            (:complaint/id complaint)
                        tz                      (-> complaint :domain/authority-group :group/tz)
                        date                    (date-util/date-in-tz (:entity/created complaint) tz)
                        category                (-> complaint
                                                    :complaint/category-group-config
                                                    (config-model/localized-name lang))
                        description             (:complaint/description complaint)
                        description-of-location (:complaint/description-of-location complaint)
                        state                   (tr [(:domain/state complaint)])]
                    (util/mmap id date category description description-of-location state complaint-id))))))))

(rf/reg-sub ::licences
  :<- [::property]
  (fn [property _]
    (->> property
         :licence/_properties
         (filter (apply du/in-state-fn licence-model/visible-licence-states))
         (sort-by gl-model/issue-date))))

(rf/reg-sub ::valid-licences
  :<- [::licences]
  (fn [licences _]
    (->> licences
         (filter licence-util/valid?))))

(rf/reg-sub ::invalid-licences
  :<- [::licences]
  (fn [licences _]
    (->> licences
         (remove licence-util/valid?))))

(rf/reg-sub ::show-invalid-licences?
  :<- [::state]
  :-> ::show-invalid-licences?)

(defn has-content? [things _]
  (-> things seq boolean))

(rf/reg-sub ::has-inspections?
  :<- [::inspections]
  has-content?)

(rf/reg-sub ::has-orders?
  :<- [::orders]
  has-content?)

(rf/reg-sub ::has-deficiencies?
  :<- [::deficiencies]
  has-content?)

(rf/reg-sub ::has-bills?
  :<- [::bills]
  has-content?)

(rf/reg-sub ::has-conditions?
  :<- [::conditions]
  has-content?)

(rf/reg-sub ::has-complaints?
  :<- [::complaints]
  has-content?)

;; Subs for V2

(rf/reg-sub ::available-content-tabs-with-counts
  :<- [::workspaces]
  :<- [::licences]
  :<- [::complaints]
  (fn [[workspaces licences complaints]]
    (cond-> (->> workspaces
                 (remove (comp ptc-model/additional-permit-type? :workspace/permit-type))
                 (group-by :workspace/permit-type)
                 (p/map-vals count))
      (seq licences) (assoc :permit-type/L (count licences))
      (empty? licences) (dissoc :permit-type/L)
      (seq complaints) (update :permit-type/CE (fnil + 0) (count complaints)))))

(rf/reg-sub ::available-content-tabs
  :<- [::available-content-tabs-with-counts]
  (fn [tabs-with-counts _]
    (->> tabs-with-counts keys sort)))

(rf/reg-sub ::selected-permit-type-tab
  :<- [::state]
  :-> ::selected-permit-type-tab)

(rf/reg-sub ::selected-workspace-attachments-permit-type-tab
  :<- [::state]
  :-> :selected-workspace-attachments-permit-type-tab)

(rf/reg-sub ::inspections-for-permit-type
  (fn [[_ permit-type] _]
    (rf/subscribe [::workspaces-by-permit-type permit-type]))
  (fn [workspaces _]
    (->> workspaces
         (mapcat workspace-model/inspections))))

(rf/reg-sub ::conditions-for-permit-type
  (fn [[_ permit-type] _]
    (rf/subscribe [::workspaces-by-permit-type permit-type]))
  (fn [workspaces _]
    (some->> workspaces
             (mapcat (fn [workspace]
                       (->> (workspace-model/workspace-target workspace)
                            condition-model/phase-conditions
                            (remove condition-model/general-condition?))))
             (group-by (comp :entity/parent :entity/parent))
             (p/map-vals conditions/sort-and-index)
             (p/map-vals (partial filter (comp (complement :application.condition/closed)
                                               second)))
             (filter (comp seq second))
             seq
             (into {}))))

(rf/reg-sub ::deficiencies-for-permit-type
  (fn [[_ permit-type] _]
    [(rf/subscribe [::inspections-for-permit-type permit-type])
     (rf/subscribe [::i18n/full-locale])
     (rf/subscribe [:language])])
  (fn [[inspections locale lang] _]
    (some->> inspections
             (mapcat inspection-model/deficiencies)
             (inspections-summary-subsection/group-deficiencies locale lang)
             seq)))

(rf/reg-sub ::orders-for-permit-type
  (fn [[_ permit-type] _]
    [(rf/subscribe [::workspaces-by-permit-type permit-type])
     (rf/subscribe [::i18n/full-locale])
     (rf/subscribe [:language])])
  (fn [[workspaces locale lang] _]
    (some->> workspaces
             (keep workspace-model/workspace-execution)
             (mapcat inspection-model/phase-orders)
             (inspections-summary-subsection/group-orders locale lang)
             seq)))

(rf/reg-sub ::bills-for-permit-type
  (fn [[_ permit-type] _]
    (rf/subscribe [::workspaces-by-permit-type permit-type]))
  (fn [workspaces _]
    (->> workspaces
         (mapcat workspace-model/phase-entities)
         (filter fee-list/show-bills-for-phase-entity?)
         (mapcat fee-model/phase-bills)
         fee-summary/group-bills
         seq)))

(rf/reg-sub ::ws+attachments-for-permit-type
  (fn [[_ permit-type] _]
    (rf/subscribe [::workspaces-by-permit-type permit-type]))
  (fn [workspaces _]
    (->> workspaces
         (keep (fn [ws]
                 (some->> ws
                          workspace-model/phase-entities
                          (mapcat attachment-model/phase-attachments)
                          seq
                          (array-map :ws ws
                                     :attachments))))
         seq)))

(rf/reg-sub ::editing?
  :<- [::state]
  :-> ::editing?)

(rf/reg-sub ::edit-details
  :<- [::state]
  :-> ::edit-details)

(rf/reg-sub ::edit-details-dirty?
  :<- [::edit-details]
  (fn [edits _]
    (boolean (seq edits))))

(rf/reg-sub ::edit-detail-value
  :<- [::edit-details]
  :<- [::property]
  (fn [[edits property] [_ k]]
    (if (contains? edits k)
      (get edits k)
      (get property k))))

(rf/reg-sub ::edit-address
  :<- [::edit-details]
  :<- [::property]
  (fn [[edits property] _]
    (if (contains? edits :address)
      (:address edits)
      (select-keys (:property/primary-address property) address-model/address-keys))))

(rf/reg-sub ::property-group
  :<- [::property]
  :-> :property/group)

(rf/reg-sub ::group-locales
  :<- [::property-group]
  i18n/group-locales-handler)

(rf/reg-sub ::group-tz
  :<- [::property-group]
  :-> :group/tz)

(rf/reg-sub ::property-attributes
  :<- [::property]
  :-> :property-attribute/_property)


(rf/reg-sub ::property-units-for-editor
  :<- [::property]
  property-model/active-or-unverified-property-units)

(rf/reg-sub ::property-unit-designators
  :<- [::property]
  :<- [:language]
  (fn [[property lang] _]
    (->> property
         :property/group
         :property-unit-designator/_group
         (filter (du/in-state-fn :property-unit-designator.state/active))
         (sort-by #(property-model/unit-designator-name % lang)))))


(rf/reg-sub ::property-attachments
  :<- [::property]
  :-> property-model/property-attachments)

(rf/reg-sub ::property-attributes-sorted
  :<- [::property-attributes]
  (fn [attributes _]
    (->> attributes
         (sort-by (comp :ordered/order :property-attribute/template)))))

(rf/reg-sub ::available-attribute-templates
  :<- [::property-group]
  :<- [::attribute-template-id-attribute-map]
  (fn [[group template-id-attr-map] _]
    (->> group
         :property-attribute-template/_group
         ;; Keep templates that enabled or are used in the property.
         (filter #(or (du/in-state? :property-attribute-template.state/enabled %)
                      (get template-id-attr-map (:db/id %))))
         (sort-by :ordered/order))))

(rf/reg-sub ::current-attribute-templates
  :<- [::property-attributes]
  (fn [attributes _]
    (->> attributes
         (keep :property-attribute/template)
         (sort-by :ordered/order))))

(rf/reg-sub ::attribute-template-id-attribute-map
  :<- [::property-attributes]
  (fn [attributes _]
    (->> attributes
         (map (fn [attr]
                [(-> attr :property-attribute/template :db/id) attr]))
         (into {}))))

(rf/reg-sub ::can-edit?
  :<- [::property]
  (fn [property _]
    (-> property :granted-permissions :permission.property/write boolean)))

(rf/reg-sub ::attachment-types
  :<- [::property-group]
  :-> :group/available-property-attachments)

(rf/reg-sub ::attachment-types-with-i18n
  :<- [::attachment-types]
  :<- [:language]
  (fn [[attachment-types lang] _]
    (->> attachment-types
         (map (juxt #(attachment-common/attachment-type-label % lang) :db/id))
         (sort-by first))))

(rf/reg-sub ::data-section-visible?
  :<- [::property-attributes]
  :<- [::can-edit?]
  :<- [::property-authority?]
  (fn [[attributes can-edit? property-authority?] _]
    (or (seq attributes)
        can-edit?
        property-authority?)))

(rf/reg-event-db ::set-selected-property-data-tab
  (fn [db [_ tab]]
    (assoc-in db [::state :selected-property-data-tab] tab)))

(rf/reg-sub ::selected-property-data-tab
  :<- [::state]
  :<- [::ff/enabled? :feature-flag.id/property-db-units]
  (fn [[state property-db-units?] _]
    (get state :selected-property-data-tab
         (if property-db-units?
           :property-view/location-and-units-tab
           :property-view/attributes-tab))))

(rf/reg-sub ::deleting-property-id
  :<- [::property-being-deleted]
  (fn [property-id _]
    property-id))

(rf/reg-sub ::property-being-deleted
  :<- [::state]
  :-> :property-being-deleted)

(rf/reg-event-db ::show-delete-confirm
  (fn [db [_ show? property-id]]
    (update db ::state
            (fn [state]
              (if show?
                (assoc state :property-being-deleted property-id)
                (dissoc state :property-being-deleted))))))

(rf/reg-sub ::property-being-verified
  :<- [::state]
  :-> :property-being-verified)

(rf/reg-sub ::property-verification-conflicts
  :<- [::state]
  :-> :property-verification-conflicts)

(rf/reg-event-db ::clear-property-verification-conflicts
  (fn [db]
    (p/dissoc-in db [::state :property-verification-conflicts])))

(rf/reg-event-db ::show-verify-confirm
  (fn [db [_ show? property-id]]
    (update db ::state
            (fn [state]
              (if show?
                (assoc state :property-being-verified property-id)
                (dissoc state :property-being-verified))))))

(rf/reg-sub ::property-management-group?
  :<- [::property]
  (fn [property _]
    (-> property :property/group (group-model/has-feature? :group.feature/property-management))))

(rf/reg-sub ::property-action-items
  :<- [::property]
  :<- [::tr]
  :<- [:session/admin?]
  :<- [::property-authority?]
  :<- [::property-management-group?]
  :<- [::can-edit?]
  :<- [::property-attachments]
  :<- [::ff/enabled? :feature-flag.id/property-verification]
  :<- [::ff/enabled? :feature-flag.id/property-data-integrity-tooling]
  :<- [:session/property-manager?]
  (fn [[property tr admin? authority? management? can-edit? attachments property-verification-enabled? data-integrity-tooling? property-manager?] _]
    (concat (when (and (or authority? ;; legacy check, :property/update-data-from-gis has been allowed to all authorities
                           can-edit?)
                       (if data-integrity-tooling?
                         (:property/id property)
                         (:property/integration-id property)))
              [{:label (tr [:property/update-data-from-gis])
                :value "update-data-from-gis"}])
            (when management?
              (concat
                (when admin?
                  [{:label (tr [(if can-edit?
                                  :process.signal/remove-admin-write-authorization
                                  :process.signal/add-admin-write-authorization)])
                    :value "toggle-write"}])
                (when can-edit?
                  [{:label (tr [(if (= (:domain/state property) :property.state/historical)
                                  :property-view/set-to-active
                                  :property-view/set-to-historical)])
                    :value "toggle-historical"}])
                (when (and (not (seq (property-model/associated-entities property)))
                           (not (seq attachments))
                           can-edit?)
                  [{:label (tr [:property-view/delete-property-text])
                    :value "delete"}])
                (when authority?
                  [{:label (tr [:process.signal/view-audit-log])
                    :value "view-audit-log"}])
                (when (and can-edit?
                           property-verification-enabled?
                           property-manager?
                           (du/in-state? :property.state/unverified property))
                  [{:label (tr [:process.signal/set-property-verified])
                    :value "verify"}]))))))

(rf/reg-event-db ::set-drawing-mode
  (fn [db [_ mode]]
    (assoc-in db [::drawing-state :mode] mode)))

(rf/reg-sub ::drawing-mode
  (fn [db _]
    (get-in db [::drawing-state :mode] :none)))

(rf/reg-event-db ::set-coordinate-edit
  (fn [db [_ property-id coordinate value]]
    (assoc-in db [::coordinate-edit-state property-id coordinate] value)))

(rf/reg-event-db ::clear-coordinate-edit
  (fn [db [_ property-id]]
    (update db ::coordinate-edit-state dissoc property-id)))

(rf/reg-event-db ::show-remove-polygon-confirm
  (fn [db [_ property-id coordinates]]
    (if coordinates
      (assoc-in db [::coordinate-edit-state :remove-polygon-confirm]
                {:property-id property-id
                 :coordinates coordinates})
      (update db ::coordinate-edit-state dissoc :remove-polygon-confirm))))

(rf/reg-sub ::coordinate-edits
  :-> ::coordinate-edit-state)

(rf/reg-sub ::remove-polygon-confirm
  :<- [::coordinate-edits]
  :-> :remove-polygon-confirm)

(rf/reg-sub ::coordinate-edit-values
  :<- [::coordinate-edits]
  (fn [edits [_ property-id coordinates]]
    (merge coordinates
           (get edits property-id))))

(rf/reg-sub ::coordinate-edit-dirty?
  :<- [::coordinate-edits]
  (fn [edits [_ property-id coordinates]]
    (let [values (merge coordinates
                        (get edits property-id))]
      (not= values coordinates))))

(rf/reg-event-db ::save-polygon-data
  (fn [db [_ property-id polygon-data]]
    (assoc-in db [::drawing-state :saved-polygons property-id] polygon-data)))

(rf/reg-sub ::saved-polygon-data
  (fn [db [_ property-id]]
    (get-in db [::drawing-state :saved-polygons property-id])))

(rf/reg-event-db ::clear-drawing-data
  (fn [db [_ property-id]]
    (update-in db [::drawing-state :saved-polygons] dissoc property-id)))

(rf/reg-event-db ::save-point-data
  (fn [db [_ property-id point-data]]
    (-> db
        (assoc-in [::drawing-state :saved-points property-id] point-data)
        (assoc-in [::coordinate-edit-state property-id] (futil/format-coordinate-pair (:coordinates point-data))))))

(rf/reg-sub ::saved-point-data
  (fn [db [_ property-id]]
    (get-in db [::drawing-state :saved-points property-id])))

(rf/reg-event-db ::clear-point-data
  (fn [db [_ property-id]]
    (-> db
        (update-in [::drawing-state :saved-points] dissoc property-id)
        (update ::coordinate-edit-state dissoc property-id))))

(rf/reg-event-fx ::save-point-to-backend
  (fn [{:keys [_]} [_ property-id geometry-point remove-polygon?]]
    {:comm/command {:command          :property/update-location
                    :method           :post
                    :data             (p/assoc-if {:property-id    property-id
                                                   :geometry-point geometry-point}
                                                  :remove-polygon? remove-polygon?)
                    :on-success       [::save-point-to-backend.success]
                    :on-error         [::save-point-to-backend.error]
                    :finally-dispatch [::save-point-to-backend.done property-id]}}))

(rf/reg-event-fx ::save-point-to-backend.success
  (fn [_ [_ status body _]]
    {:fx [[:dispatch [::entities/apply-changes status body]]
          [:dispatch [:notifications/add :toast :success :success :property/location-saved]]]}))

(rf/reg-event-fx ::save-point-to-backend.error
  (fn [_ _]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property/location-save-error]]]}))

(rf/reg-event-fx ::save-point-to-backend.done
  (fn [{:keys [db]} [_ property-id]]
    {:db (assoc-in db [::drawing-state :saving?] false)
     :fx [[:dispatch [::set-drawing-mode :none]]
          [:dispatch [::clear-point-data property-id]]
          [:dispatch [::clear-coordinate-edit property-id]]
          [:dispatch [::clear-drawing-data property-id]]
          [:dispatch [::clear-geocoded-reference]]]}))

(rf/reg-event-db ::set-saving-location
  (fn [db [_ saving?]]
    (assoc-in db [::drawing-state :saving?] saving?)))

(rf/reg-sub ::saving-location?
  (fn [db _]
    (get-in db [::drawing-state :saving?] false)))

(rf/reg-event-fx ::save-polygon-to-backend
  (fn [_ [_ property-id geometry]]
    {:comm/command {:command          :property/update-location
                    :method           :post
                    :data             {:property-id property-id :geometry geometry}
                    :on-success       [::save-polygon-to-backend.success]
                    :on-error         [::save-polygon-to-backend.error]
                    :finally-dispatch [::save-polygon-to-backend.done property-id]}}))

(rf/reg-event-fx ::save-polygon-to-backend.success
  (fn [_ [_ status body _]]
    {:fx [[:dispatch [::entities/apply-changes status body]]
          [:dispatch [:notifications/add :toast :success :success :property/polygon-saved]]]}))

(rf/reg-event-fx ::save-polygon-to-backend.error
  (fn [_ _]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property/polygon-save-failed]]]}))

(rf/reg-event-fx ::save-polygon-to-backend.done
  (fn [{:keys [db]} [_ property-id]]
    {:db (assoc-in db [::drawing-state :saving?] false)
     :fx [[:dispatch [::set-drawing-mode :none]]
          [:dispatch [::clear-point-data property-id]]
          [:dispatch [::clear-coordinate-edit property-id]]
          [:dispatch [::clear-drawing-data property-id]]
          [:dispatch [::clear-geocoded-reference]]]}))

(rf/reg-event-fx ::select-address-result
  (fn [{:keys [db]} [_ address-result]]
    (let [coordinates (:geometry-point address-result)]
      {:db (-> db
               (assoc-in [::address-search-state :selected-coordinates] coordinates)
               (assoc-in [::address-search-state :selected-address-result] address-result))
       :fx [[:dispatch [::set-map-center coordinates]]
            [:dispatch [::clear-geocoded-reference]]]})))

(rf/reg-sub ::selected-search-coordinates
  (fn [db _]
    (get-in db [::address-search-state :selected-coordinates])))

(rf/reg-event-db ::set-map-center
  (fn [db [_ coordinates]]
    (assoc-in db [::map-state :custom-center] coordinates)))

(rf/reg-event-db ::reset-map-center
  (fn [db _]
    (update db ::map-state dissoc :custom-center)))

(rf/reg-event-db ::clear-address-search-result
  (fn [db _]
    (update db ::address-search-state dissoc :selected-address-result)))

(rf/reg-sub ::custom-map-center
  (fn [db _]
    (get-in db [::map-state :custom-center])))

(rf/reg-sub ::map-center
  :<- [::custom-map-center]
  :<- [::selected-search-coordinates]
  :<- [::editing?]
  (fn [[custom-center selected-search-coords editing?] [_ property municipality]]
    (let [property-center     (when property
                                (location-model/property-location->center-coords property))
          municipality-center (when municipality
                                (municipality-model/municipality-center-coords municipality))
          default-center      (or property-center municipality-center)]
      (or custom-center
          (when editing? selected-search-coords)
          default-center))))

(rf/reg-event-fx ::search-error
  (fn [{:keys [_]} [_ _]]
    {:dispatch [:notifications/add :toast :error :error/connection-error-title :error/connection-error-description]}))

(rf/reg-sub ::selected-search-address-result
  (fn [db _]
    (get-in db [::address-search-state :selected-address-result])))

(rf/reg-sub ::address-search-value
  (fn [db _]
    (get-in db [::gis-search/address-search-results :search-value])))

(rf/reg-sub ::address-search-has-results?
  (fn [db _]
    (let [results (get-in db [::gis-search/address-search-results :results])]
      (not (empty? results)))))

(rf/reg-sub ::address-search-pending?
  (fn [db _]
    (get-in db [::gis-search/address-search-results :pending?])))

(rf/reg-sub ::search-started?
  (fn [db _]
    (get-in db [::address-search-state :search-started?] false)))

(rf/reg-event-db ::set-search-started
  (fn [db [_ started?]]
    (assoc-in db [::address-search-state :search-started?] started?)))

(rf/reg-event-db ::set-previous-search-value
  (fn [db [_ value]]
    (assoc-in db [::address-search-state :previous-search-value] value)))

(rf/reg-sub ::can-geolocate?
  :<- [::address-search-value]
  :<- [::address-search-has-results?]
  :<- [::selected-search-address-result]
  :<- [::address-search-pending?]
  :<- [::search-started?]
  (fn [[search-value has-results? selected-result search-pending? search-started?] _]
    (and (seq search-value)
         (>= (count search-value) min-address-search-length)
         (not search-pending?)
         (not has-results?)
         (not selected-result)
         search-started?)))


(rf/reg-sub ::geocoded-reference-point
  (fn [db _]
    (get-in db [::address-search-state :geocoded-reference-point])))


(rf/reg-event-db ::clear-geocoded-reference
  (fn [db _]
    (update db ::address-search-state dissoc :geocoded-reference-point)))

(rf/reg-event-fx ::geolocate-address
  (fn [{:keys [db]} [_ property-id search-text]]
    (let [ctx          (entities/db->context db)
          property     (entities/find-entity ctx property-id)
          lang         (:language db i18n-model/default-lang)
          municipality (property-model/municipality-name lang property)
          full-address (if municipality
                         (str search-text ", " municipality)
                         search-text)
          country      (-> property :property/municipality :municipality/country name)]
      {:comm/command {:command    :property/geolocate-address
                      :method     :post
                      :data       {:property-id property-id
                                   :address     full-address
                                   :country     country}
                      :on-success [::geolocate-address.success]
                      :on-error   [::geolocate-address.error]}})))

(rf/reg-event-fx ::geolocate-address.success
  (fn [{:keys [db]} [_ _ {:keys [location-data]}]]
    (let [point-coords (-> location-data :point :coordinates)]
      {:db (-> db
               (assoc-in [::address-search-state :geocoded-reference-point] point-coords)
               (assoc-in [::map-state :custom-center] point-coords))
       :fx [[:dispatch [:notifications/add :toast :info "" :property/geolocate-address-reference-shown]]]})))

(rf/reg-event-fx ::geolocate-address.error
  (fn [_ [_ _]]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property/geolocate-address-failed]]]}))

(rf/reg-event-db ::show-subdivision-modal
  (fn [db [_ show?]]
    (assoc-in db [::subdivision-modal :open?] show?)))

(rf/reg-sub ::subdivision-modal-open?
  (fn [db _]
    (get-in db [::subdivision-modal :open?] false)))

(rf/reg-event-db ::set-subdivision-form-data
  (fn [db [_ field value]]
    (assoc-in db [::subdivision-modal :form-data field] value)))

(rf/reg-sub ::subdivision-form-data
  (fn [db _]
    (get-in db [::subdivision-modal :form-data] {})))

(rf/reg-event-fx ::create-subdivision-property
  (fn [{:keys [db]} [_]]
    (let [form-data          (get-in db [::subdivision-modal :form-data])
          ctx                (entities/db->context db)
          property-id        (get-in db [::state :property-id])
          property           (entities/find-entity ctx property-id)
          parent-property-id (:db/id property)
          domain-group-id    (get-in property [:property/group :db/id])
          municipality-id    (get-in property [:property/municipality :db/id])]
      {:comm/command {:command    :property/create
                      :method     :post
                      :data       (cond-> {:domain-group-id          domain-group-id
                                           :municipality-id          municipality-id
                                           :parent-property-id       parent-property-id
                                           :parent-relationship-type :property-relationship/subdivision}
                                    (not (str/blank? (:property-id form-data)))
                                    (assoc :property-id (:property-id form-data)))
                      :on-success [::create-subdivision-property.success]
                      :on-error   [::create-subdivision-property.error]}})))

(rf/reg-event-fx ::create-subdivision-property.success
  (fn [{:keys [db]} [_ _ {:keys [property-id]}]]
    {:db (-> db
             (assoc-in [::subdivision-modal :open?] false)
             (assoc-in [::subdivision-modal :form-data] {}))
     :fx [[:dispatch [:notifications/add :toast :success :success :property-view/subdivision-created-successfully]]
          [:route/push {:route  :view/property
                        :params {:db-id property-id}}]]}))

(rf/reg-event-fx ::create-subdivision-property.error
  (fn [{:keys [_]} [_ _]]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property-view/subdivision-creation-failed]]]}))

(rf/reg-event-fx ::get-property-relations
  (fn [{:keys [db]} [_ property-id]]
    {:db           (assoc-in db [::relations :loading?] true)
     :comm/command {:command    :property/get-relations
                    :method     :get
                    :data       {:property-id property-id}
                    :on-success [::get-property-relations.success]
                    :on-error   [::get-property-relations.error]}}))

(rf/reg-event-db ::get-property-relations.success
  (fn [db [_ _ relations]]
    (-> db
        (assoc-in [::relations :data] relations)
        (assoc-in [::relations :loading?] false))))

(rf/reg-event-db ::get-property-relations.error
  (fn [db [_ _]]
    (-> db
        (assoc-in [::relations :data] [])
        (assoc-in [::relations :loading?] false))))

(rf/reg-sub ::property-relations
  (fn [db _]
    (get-in db [::relations :data] [])))

(rf/reg-sub ::property-relations-loading?
  (fn [db _]
    (get-in db [::relations :loading?] false)))

(rf/reg-event-db ::toggle-relation-selection
  (fn [db [_ entity-id selected?]]
    (if selected?
      (update-in db [::relations :selected-relations] (fnil conj #{}) entity-id)
      (update-in db [::relations :selected-relations] (fnil disj #{}) entity-id))))

(rf/reg-sub ::selected-relations
  (fn [db _]
    (get-in db [::relations :selected-relations] #{})))

(rf/reg-sub ::has-selected-relations?
  :<- [::selected-relations]
  :-> seq)

(rf/reg-event-fx ::set-properties-to-historical
  (fn [_ [_ property-ids]]
    {:comm/command {:command    :property/force-properties-as-historical
                    :method     :post
                    :data       {:property-ids property-ids}
                    :on-success [::toggle-state.success :property.state/historical]
                    :on-error   [::toggle-state.error]}}))

(rf/reg-event-fx ::set-properties-to-active
  (fn [_ [_ property-ids]]
    {:comm/command {:command    :property/force-properties-as-active
                    :method     :post
                    :data       {:property-ids property-ids}
                    :on-success [::toggle-state.success :property.state/active]
                    :on-error   [::toggle-state.error]}}))

(rf/reg-event-fx ::set-single-property-to-historical
  (fn [_ [_ property-id]]
    {:comm/command {:command    :property/force-properties-as-historical
                    :method     :post
                    :data       {:property-ids [property-id]}
                    :on-success [::single-property-toggle-state.success :property.state/historical]
                    :on-error   [::toggle-state.error]}}))

(rf/reg-event-fx ::set-single-property-to-active
  (fn [_ [_ property-id]]
    {:comm/command {:command    :property/force-properties-as-active
                    :method     :post
                    :data       {:property-ids [property-id]}
                    :on-success [::single-property-toggle-state.success :property.state/active]
                    :on-error   [::toggle-state.error]}}))

(rf/reg-event-fx ::single-property-toggle-state.success
  (fn [{:keys [db]} [_ state-type status response]]
    (let [message-key (case state-type
                        :property.state/historical :property-view/property-marked-historic-message
                        :property.state/active :property-view/property-marked-active-message)]
      {:db (assoc-in db [::relations :selected-relations] #{})
       :fx [[:dispatch [::entities/apply-changes status response]]
            [:dispatch [:notifications/add :toast :success :success message-key]]]})))

(rf/reg-event-fx ::toggle-state.success
  (fn [{:keys [db]} [_ state-type status response]]
    (let [message-key (case state-type
                        :property.state/historical :property-view/property-marked-historic-message
                        :property.state/active :property-view/property-marked-active-message)]
      {:db (assoc-in db [::relations :selected-relations] #{})
       :fx [[:dispatch [::entities/apply-changes status response]]
            [:dispatch [:notifications/add :toast :success :success message-key]]
            [:dispatch [::get-property-relations (get-in db [::state :property-id])]]]})))

(rf/reg-event-fx ::toggle-state.error
  (fn [_ _]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property-view/relations-state-toggle-failed]]]}))

(rf/reg-event-fx ::show-merge-modal
  (fn [{:keys [db]} [_ show?]]
    (if show?
      {:db (assoc-in db [::merge-modal :open?] true)}
      {:db (assoc db ::merge-modal {:open?     false
                                    :form-data {:properties []}})
       :fx [[:dispatch [::gis-search/clear-address-search]]]})))

(rf/reg-sub ::merge-modal-open?
  (fn [db _]
    (get-in db [::merge-modal :open?] false)))

(rf/reg-sub ::merge-form-data
  (fn [db _]
    (get-in db [::merge-modal :form-data] {:properties []})))

(rf/reg-event-fx ::add-selected-merge-property
  (fn [{:keys [db]} [_ property-result]]
    (let [current-props   (get-in db [::merge-modal :form-data :properties] [])
          property-result (select-keys property-result [:property :address :municipality])
          property-id     (-> property-result :property :property/id)
          selected?       (some #(= (-> % :property :property/id) property-id)
                                current-props)]

      (if (and property-id (not selected?))
        (let [properties (conj current-props property-result)
              db         (assoc-in db [::merge-modal :form-data :properties] properties)
              command    {:command    :property/find
                          :data       {:search-term property-id}
                          :on-success [::search-property-by-id.success property-id]
                          :on-error   [::search-property-by-id.error property-id]}]
          {:db           db
           :comm/command command})
        {:db db}))))

(rf/reg-event-fx ::search-property-by-id.success
  (fn [_ [_ _ _ {:keys [results total-count]}]]
    (if (and results (> total-count 0))
      (let [result      (first results)
            id          (:id result)
            roll-id     (or (:property/verified-id result)
                            (:property/id result))
            id-domain   (:property/id-domain result)
            verified-id (:property/verified-id result)]
        {:fx [[:dispatch [:comm/command-monitored {:command    :property/get
                                                   :method     :get
                                                   :data       (util/mmap id-domain verified-id)
                                                   :on-success [::fetch-complete-property.success id roll-id]
                                                   :on-error   [::fetch-complete-property.error id]}]]]})
      {})))

(rf/reg-event-fx ::search-property-by-id.error
  (fn [_ _]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property-view/property-search-error]]]}))

(rf/reg-event-fx ::fetch-complete-property.success
  (fn [{:keys [db]} [_ _id roll-id _status {:keys [property-id entities]}]]
    (let [ctx             (du/entities->entity-db entities)
          property        (get ctx property-id)
          location-id     (-> property :entity/location)
          location        (when location-id
                            (get ctx location-id))
          location-center (when location
                            {:x (-> location :location.center/x)
                             :y (-> location :location.center/y)})
          location-shape  (-> location :location/shape)
          current-props   (get-in db [::merge-modal :form-data :properties] [])
          updated-props   (mapv (fn [prop]
                                  (if (= (get-in prop [:property :property/id]) roll-id)
                                    (cond-> (assoc prop :db-id property-id)
                                      (and location-center location-shape)
                                      (assoc :location-center location-center
                                             :location-shape location-shape))
                                    prop))
                                current-props)]
      {:db (assoc-in db [::merge-modal :form-data :properties] updated-props)})))

(rf/reg-event-fx ::fetch-complete-property.error
  (fn [_ _]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property-view/property-fetch-error]]]}))

(rf/reg-event-db ::remove-selected-merge-property
  (fn [db [_ index]]
    (let [properties (get-in db [::merge-modal :form-data :properties] [])
          properties (concat (take index properties)
                             (drop (inc index) properties))]
      (assoc-in db [::merge-modal :form-data :properties] properties))))

(rf/reg-event-fx ::complete-merge-selections
  (fn [{:keys [db]} [_ parent-property]]
    (let [selected-props      (get-in db [::merge-modal :form-data :properties] [])
          domain-group-id     (-> parent-property :property/group :db/id)
          municipality-id     (-> parent-property :property/municipality :db/id)
          merge-candidate-ids (concat [(:db/id parent-property)]
                                      (keep :db-id selected-props))]
      (if (seq selected-props)
        {:db           (assoc-in db [::merge-modal :loading?] true)
         :comm/command {:command    :property/merge
                        :method     :post
                        :data       {:merge-candidate-ids merge-candidate-ids
                                     :domain-group-id     domain-group-id
                                     :municipality-id     municipality-id}
                        :on-success [::complete-merge-selections.success]
                        :on-error   [::complete-merge-selections.error]}}
        {:fx [[:dispatch [:notifications/add :toast :warning :warning :property-view/no-properties-selected]]]}))))

(rf/reg-event-fx ::complete-merge-selections.success
  (fn [{:keys [db]} [_ _status {:keys [property-id]}]]
    {:db         (-> db
                     (assoc-in [::merge-modal :open?] false)
                     (assoc-in [::merge-modal :loading?] false)
                     (assoc-in [::merge-modal :form-data] {:properties []}))
     :fx         [[:dispatch [:notifications/add :toast :success :success :property-view/merge-properties-success]]]
     :route/push {:route :view/property :params {:db-id property-id}}}))

(rf/reg-event-fx ::complete-merge-selections.error
  (fn [{:keys [db]} [_ _error]]
    {:db (-> db
             (assoc-in [::merge-modal :loading?] false))
     :fx [[:dispatch [:notifications/add :toast :error :error/error :property-view/merge-properties-error]]]}))

(rf/reg-event-fx ::merge-search-error
  (fn [_ [_ _]]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :property-view/property-search-error]]]}))

(rf/reg-sub ::merge-modal-map-center
  (fn [_ [_ parent-property]]
    (let [property-center     (location-model/property-location->center-coords parent-property)
          municipality-center (municipality-model/municipality-center-coords
                                (-> parent-property :property/municipality))]
      (or property-center municipality-center))))

(rf/reg-sub ::merge-modal-markers
  (fn [db [_ parent-property]]
    (let [parent-center    (location-model/property-location->center-coords parent-property)
          selected-props   (get-in db [::merge-modal :form-data :properties])
          property-centers (keep (fn [prop]
                                   (when-let [center (:location-center prop)]
                                     [(:x center) (:y center)]))
                                 selected-props)
          centers          (concat [parent-center] property-centers)]
      centers)))

(rf/reg-sub ::merge-modal-parent-shape
  (fn [_ [_ parent-property]]
    (-> parent-property :entity/location :location/shape)))

(rf/reg-sub ::merge-modal-merged-shapes
  (fn [db _]
    (let [selected-props   (get-in db [::merge-modal :form-data :properties])
          shapes-with-data (keep :location-shape selected-props)]
      (when (seq shapes-with-data)
        {:type     "FeatureCollection"
         :features (mapv property-model/geojson-feature shapes-with-data)}))))

(rf/reg-sub ::property-uses-system-database?
  :<- [::property]
  (fn [property _]
    (= :gis-backend/elasticsearch
       (-> property :property/group :group/property-data-source))))


(rf/reg-sub ::back-href
  :<- [:route/match]
  :<- [::ff/enabled? :feature-flag.id/workspace-search-updates]
  (fn [[{{:keys [workspace-ref
                 complaint-ref
                 property-ref
                 modal]}       :query-params}
        workspace-search-updates?]]
    (let [workspace   (when workspace-ref
                        @(rf/subscribe [::entities/entity {:workspace/id workspace-ref}]))
          complaint   (when complaint-ref
                        @(rf/subscribe [::entities/entity {:complaint/id complaint-ref}]))
          management? @(rf/subscribe [::property-management-group?])]
      ;; `property-ref` is the most recent hop (property -> related property), so it wins over the
      ;; workspace/complaint the chain may have started from.
      (cond property-ref (href/href :view/property {:db-id property-ref})
            workspace (ws-util/workspace-href workspace)
            complaint (href/href :view/code-enforcement-complaints nil (p/assoc-if {:complaint-ref (:complaint/id complaint)}
                                                                                   :modal modal))
            management? (if workspace-search-updates?
                          (href/href :view/search nil {:selected-tab "property"})
                          (href/href :view/property-dashboard))
            :else (href/href :dashboard)))))


(rf/reg-event-fx ::show-audit-log-view
  (fn [_ [_ property-id]]
    {:route/push {:route  :view/property-audit-log
                  :params {:db-id property-id}}}))


(defn get-event [property {:keys [value]}]
  (let [can-edit? (-> property :granted-permissions :permission.property/write)]
    (case value
      "toggle-write" [::admin-permissions/set-property-write-authorization
                      property
                      (boolean can-edit?)]
      "toggle-historical" (if (= (:domain/state property) :property.state/historical)
                            [::set-single-property-to-active
                             (:db/id property)]
                            [::set-single-property-to-historical
                             (:db/id property)])
      "delete" [::show-delete-confirm true (:db/id property)]
      "view-audit-log" [::show-audit-log-view (:db/id property)]
      "update-data-from-gis" [::update-data-from-gis (:db/id property)]
      "verify" [::show-verify-confirm true (:db/id property)])))


(rf/reg-sub ::view-data
  :<- [::back-href]
  :<- [::property]
  :<- [::property-action-items]
  (fn [[back-href property action-items]]
    {:back-href back-href
     :options   {:items (->> action-items
                             (map (fn [item]
                                    {:label    (:label item)
                                     :on-click #(>evt (get-event property item))})))}}))
